import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';

// Shared table controls: <Pagination/> (Previous / page numbers / Next / total) and a
// debounced <SearchBox/>. Styled to match the app's existing inline-style look.

const btn = (active, disabled) => ({
    minWidth: '32px', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700',
    border: active ? '1px solid #2563eb' : '1px solid #d1d5db',
    background: active ? '#2563eb' : '#fff', color: active ? '#fff' : disabled ? '#cbd5e1' : '#334155',
    cursor: disabled ? 'not-allowed' : 'pointer'
});

// 1 … 4 5 [6] 7 8 … 20
export function pageWindow(page, totalPages, span = 2) {
    const pages = new Set([1, totalPages]);
    for (let p = page - span; p <= page + span; p++) if (p >= 1 && p <= totalPages) pages.add(p);
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((p, i) => {
        if (i > 0 && p - sorted[i - 1] > 1) out.push(p - sorted[i - 1] === 2 ? sorted[i - 1] + 1 : '…' + p);
        out.push(p);
    });
    return out;
}

export function Pagination({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange, loading = false, pageSizes = [25, 50, 100], label = 'records' }) {
    const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const last = Math.min(total, page * pageSize);
    return (
        <div data-testid="pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', margin: '12px 0', fontSize: '12px', color: '#475569' }}>
            <div data-testid="pagination-summary" style={{ fontWeight: '600' }}>
                {total === 0 ? `0 ${label}` : `Showing ${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()} ${label}`}
                {loading && <span style={{ marginLeft: '8px', color: '#94a3b8' }}>Loading…</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                {onPageSizeChange && (
                    <select aria-label="Rows per page" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        style={{ padding: '5px 6px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', background: '#fff' }}>
                        {pageSizes.map((n) => <option key={n} value={n}>{n} / page</option>)}
                    </select>
                )}
                <button type="button" data-testid="page-prev" disabled={page <= 1} onClick={() => onPageChange(page - 1)} style={btn(false, page <= 1)}>Previous</button>
                {pageWindow(page, totalPages).map((p) => (typeof p === 'string'
                    ? <span key={p} style={{ padding: '0 2px', color: '#94a3b8' }}>…</span>
                    : <button type="button" key={p} data-testid={`page-${p}`} aria-current={p === page ? 'page' : undefined} onClick={() => onPageChange(p)} style={btn(p === page, false)}>{p}</button>))}
                <button type="button" data-testid="page-next" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} style={btn(false, page >= totalPages)}>Next</button>
            </div>
        </div>
    );
}

// Debounced search: typing updates the box instantly, `onChange` fires 300ms after the last keystroke
// (so a request is not sent per keystroke). `value` resets the box when cleared from outside.
export function SearchBox({ value, onChange, placeholder = 'Search...', delay = 300, minWidth = '220px' }) {
    const [text, setText] = useState(value || '');
    const timer = useRef(null);
    const last = useRef(value || '');
    useEffect(() => { if ((value || '') !== last.current) { last.current = value || ''; setText(value || ''); } }, [value]);
    useEffect(() => () => clearTimeout(timer.current), []);
    const push = (v) => { last.current = v; onChange(v); };
    return (
        <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
                type="text" aria-label="Search" value={text} placeholder={placeholder}
                onChange={(e) => { const v = e.target.value; setText(v); clearTimeout(timer.current); timer.current = setTimeout(() => push(v), delay); }}
                style={{ padding: '7px 26px 7px 28px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', minWidth }}
            />
            {text && (
                <button type="button" aria-label="Clear search" onClick={() => { clearTimeout(timer.current); setText(''); push(''); }}
                    style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', padding: 2, display: 'flex' }}>
                    <X size={12} />
                </button>
            )}
        </div>
    );
}

// Plain debounced text input (optionally with a <datalist> via `list`) for per-column filters.
export function DebouncedInput({ value, onChange, placeholder, list, delay = 300, style, 'aria-label': ariaLabel }) {
    const [text, setText] = useState(value || '');
    const timer = useRef(null);
    const last = useRef(value || '');
    useEffect(() => { if ((value || '') !== last.current) { last.current = value || ''; setText(value || ''); } }, [value]);
    useEffect(() => () => clearTimeout(timer.current), []);
    return (
        <input
            type="text" list={list} value={text} placeholder={placeholder} aria-label={ariaLabel || placeholder} style={style}
            onChange={(e) => { const v = e.target.value; setText(v); clearTimeout(timer.current); timer.current = setTimeout(() => { last.current = v; onChange(v); }, delay); }}
        />
    );
}

export default Pagination;